import { z } from 'zod'

/**
 * Error body for every non-2xx `/api/*` response:
 *   { "error": { "code": "validation_error", "message": "…", "details": … } }
 */
export const API_ERROR_CODES = [
  'bad_request',
  'validation_error',
  'unauthorized',
  'forbidden',
  'not_found',
  'conflict',
  'rate_limited',
  'payload_too_large',
  'internal',
  'unavailable',
  // domain codes
  'email_taken', // organizer signup
  'invalid_credentials',
  'not_implemented', // 501: route mounted, implementation pending (Phase 0 skeleton)
  'duplicate_email', // member registration, same organization
  'duplicate_national_id',
  'registration_closed', // 409: the organization is not published (nobody can join)
  'requirements_not_met', // declared data fails the organization requirements (age / country / contact)
  'invalid_registration_token',
  'kyc_not_approved',
  'kyc_retry_not_allowed',
  'kyc_attempts_exhausted',
  'account_exists',
  'election_not_editable', // H3 structural lock (votings)
  'organization_not_editable', // 409: test mode / slug after publish (details.fields)
  'slug_taken', // 409: organization slug already used (voting slugs are generated)
  'invalid_state_transition',
  'voting_closed',
  'sequencer_unavailable',
  'didit_unavailable', // 503: Didit unreachable / 5xx (temporary, retry later)
  'didit_config_rejected', // 422: Didit refused the organization's workflow (4xx): details = DiditRejection
  'verification_misconfigured', // 409: voter side of didit_config_rejected (not retryable, contact the organizer)
  'invalid_signature',
  'challenge_expired',
  'invalid_signup_code', // organizer signup when ORG_SIGNUP_CODE is set
  'quota_exceeded', // 429: per-organizer / global process-creation quotas, questions per election
  'registration_completed', // 403: the registration token's account exists ⇒ log in instead
  'registration_token_expired',
  'identity_final_decision', // 409: a declined_final member with this email / national ID exists (L3')
  'member_removed', // 409 at join / 403 at login (correct password only): the organizer removed this member
  'member_reset', // 403 at login (correct password only): the membership was reset ⇒ register again
  'not_eligible', // 403 at CSP: details.reason = IneligibilityReason (district / age / not active / removed)
  'residence_declaration_required', // 403: CSP attestation without the residence declaration the voting requires
  // contact verification (api/contact.ts)
  'duplicate_phone', // 409: the phone belongs to another member of this organization
  'invalid_code', // 422: wrong code (details.attemptsLeft)
  'code_expired', // 422: no pending code (expired or never sent): send a new one
  'code_attempts_exceeded', // 422: too many wrong codes: send a new one
  'email_disposable', // 422
  'email_undeliverable', // 422
  'email_rejected', // 422: blocklisted / high risk
  'invalid_email', // 422: Didit refused the address
  'phone_disposable', // 422
  'phone_rejected', // 422: blocked / high-risk number
  'invalid_phone', // 422: Didit refused the number
  'contact_not_verified', // 400: the email / phone proof is missing, expired or for another address
  'resend_too_soon', // 429: details.retryAfterSeconds
  'contact_verification_unavailable', // 503: Didit refused the channel (e.g. phone before the first top-up)
  // email (api/communications.ts)
  'email_unavailable', // 503: sending failed for good / transport misconfigured (test send)
  'invalid_unsubscribe_token', // 404: unknown / tampered opt-out token
  'no_recipients', // 422: the audience has no member to email
  // election keys and committees (api/councils.ts)
  'key_mode_not_allowed', // 403: the server-held sequencer key outside its gate (env flag + test org + allowlist)
  'key_mode_unavailable', // 409: the registry has no adapter for the chosen key mode (DKG / Council)
  'committee_not_ready', // 409: the voting's committee is not Live with both grants (or not this organization's)
  'council_unavailable', // 503: committees disabled, or the council subsystem failed closed (deployment mismatch)
  'council_already_linked', // 409: reserved (committees are linked per organization; a race on the same link)
  'council_not_authorized', // 422: pasted committee does not authorize this organization's creator address
  'unknown_committee', // 404: no such ceremony on the deployment's Council manager
  'fingerprint_mismatch', // 409: the confirmed fingerprint differs from the committee on chain
  'creator_wallet_unavailable', // 503: the organization's creator wallet cannot be derived / does not match
  // PLAN v4 (modular requirements, geocoding)
  'geocoder_unavailable', // 503: Nominatim unreachable / rate-limited (organizer area editor)
  'geo_no_boundary', // 422: the picked place has no polygon (a point): use a circle area instead
  'matched_member_active', // 409: approve_same_person with a matched member that is not revoked / removed
  'slot_in_use', // 409: another member who votes (or is about to) holds this ballot slot (approval, takeover, account)
] as const
export const ApiErrorCodeSchema = z.enum(API_ERROR_CODES)
export type ApiErrorCode = z.infer<typeof ApiErrorCodeSchema>

export const ApiErrorBodySchema = z.object({
  error: z.object({
    code: z.string(),
    message: z.string(),
    /** For validation_error: zod `issues` (path + message); otherwise free-form. */
    details: z.unknown().optional(),
  }),
})
export type ApiErrorBody = z.infer<typeof ApiErrorBodySchema>

/**
 * Didit refused the organization's verification workflow (4xx on create / update), e.g. phone verification on a
 * live account before its first top-up. `features` are Didit's feature node keys (e.g. `phone_verification`);
 * `requirement` is the organization requirement to change, when known.
 */
export const DiditRejectionSchema = z.object({
  message: z.string(),
  diditMessage: z.string(),
  features: z.array(z.string()),
  /** Requirement to change (`phone`: v1; the module ids since PLAN v4). */
  requirement: z.enum(['phone', 'uniqueHuman', 'identityDocument', 'citizenship', 'residence']).nullable(),
})
export type DiditRejection = z.infer<typeof DiditRejectionSchema>

export const PaginationQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(200).default(50),
})
export type PaginationQuery = z.infer<typeof PaginationQuerySchema>

export const paginated = <T extends z.ZodType>(item: T) =>
  z.object({
    items: z.array(item),
    total: z.number().int().min(0),
    page: z.number().int().min(1),
    pageSize: z.number().int().min(1),
  })

/** Header carrying the member registration token (kept out of URLs / logs). */
export const REGISTRATION_TOKEN_HEADER = 'x-registration-token'
/** Cookie names (httpOnly, SameSite=Lax). The member session is scoped to ONE organization (claim `oid`). */
export const ORGANIZER_COOKIE = 'org_session'
export const MEMBER_COOKIE = 'member_session'
